import { ChangeDetectionStrategy, Component, ViewEncapsulation, computed, input, output, signal } from '@angular/core';
import { ButtonComponent, ButtonDisplayStyle } from '@workbuddy/ui/v2/components/button';
import type { SuggestActionsConfig, SuggestGroup, SuggestGroupId } from '../../agent.types';

/**
 * Welcome-screen suggestion UI: 4 always-visible category tiles.
 * Clicking a tile expands a row of prompt chips below; clicking an active tile collapses it.
 * Clicking a chip emits the prompt string via (promptSelected) for the parent to fill the input.
 *
 * Active tile uses Primary display style; inactive uses Secondary. Chip row animates via CSS.
 */
@Component({
  selector: 'agent-suggest-actions',
  standalone: true,
  imports: [ButtonComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  templateUrl: './suggest.actions.component.html',
  styleUrls: ['./suggest.actions.component.less'],
})
export class AgentSuggestActionsComponent {

  protected readonly ButtonDisplayStyle = ButtonDisplayStyle;

  public readonly $config = input.required<SuggestActionsConfig>({ alias: 'config' });

  public readonly promptSelected = output<string>();

  protected readonly $activeGroupId = signal<SuggestGroupId | null>(null);

  protected readonly $activeGroup = computed<SuggestGroup | null>(() => {

    const id = this.$activeGroupId();
    if (!id) return null;
    return this.$config().groups.find(g => g.id === id) ?? null;
  });

  protected onTileClick(id: SuggestGroupId): void {

    this.$activeGroupId.update(current => current === id ? null : id);
  }

  protected onPromptClick(prompt: string): void {

    this.$activeGroupId.set(null);
    this.promptSelected.emit(prompt);
  }

  protected displayStyleFor(id: SuggestGroupId): ButtonDisplayStyle {

    return this.$activeGroupId() === id ? ButtonDisplayStyle.Primary : ButtonDisplayStyle.Secondary;
  }
}
